/**
 * Adventure Progress Manager
 * アドベンチャーモードの進行管理
 *
 * Created: 2025-12-17
 */

import type {
  AdventureProgress,
  NpcBattleRecord,
  NpcId,
  BattleResult,
} from '../data/types';
import {
  loadProgress,
  saveProgress,
  createInitialProgress,
  createInitialBattleRecord,
  updateNpcRecord,
  addClearedNpc,
  addTitle,
  updateStats,
} from '../data/storage';
import { getNpcConfig, DAILY_BATTLE_LIMIT } from '../data/constants';

// ============================================================
// 進行データの取得
// ============================================================

/**
 * 現在の進行データを取得
 */
export function getProgress(): AdventureProgress {
  return loadProgress();
}

/**
 * 進行データを初期化
 */
export function initProgress(): AdventureProgress {
  return createInitialProgress();
}

/**
 * NPCの対戦記録を取得
 */
export function getNpcRecord(
  progress: AdventureProgress,
  npcId: NpcId
): NpcBattleRecord {
  return progress.npcRecords[npcId] || createInitialBattleRecord();
}

/**
 * NPCがクリア済みかどうか
 */
export function isNpcCleared(progress: AdventureProgress, npcId: NpcId): boolean {
  return progress.clearedNpcs.includes(npcId);
}

/**
 * NPCが初回対戦かどうか
 */
export function isFirstBattle(progress: AdventureProgress, npcId: NpcId): boolean {
  const record = progress.npcRecords[npcId];
  return !record || record.firstClearDate === null;
}

// ============================================================
// 対戦結果の記録
// ============================================================

/**
 * 対戦結果を記録
 * @returns 更新された進行データ
 */
export function recordBattle(
  progress: AdventureProgress,
  npcId: NpcId,
  result: BattleResult
): AdventureProgress {
  const now = new Date();
  const today = now.toISOString().split('T')[0];
  const isWin = result === 'win';
  const wasFirstClear = isFirstBattle(progress, npcId);

  // NPC記録を更新
  let updated = updateNpcRecord(progress, npcId, (record) => ({
    ...record,
    wins: isWin ? record.wins + 1 : record.wins,
    losses: isWin ? record.losses : record.losses + 1,
    firstClearDate:
      isWin && !record.firstClearDate ? now.toISOString() : record.firstClearDate,
    lastBattleDate: now.toISOString(),
    todayBattleCount: record.todayBattleCount + 1,
  }));

  // 勝利時、クリア済みリストに追加
  if (isWin && wasFirstClear) {
    updated = addClearedNpc(updated, npcId);

    // 初回クリア報酬の称号を付与
    const config = getNpcConfig(npcId);
    if (config?.firstClearReward.title) {
      updated = addTitle(updated, config.firstClearReward.title);
    }
  }

  // 統計を更新
  updated = updateStats(updated, result);

  // 保存
  saveProgress(updated);

  return updated;
}

/**
 * 報酬獲得回数を記録（再戦報酬用）
 */
export function recordRewardClaimed(
  progress: AdventureProgress,
  npcId: NpcId
): AdventureProgress {
  const updated = updateNpcRecord(progress, npcId, (record) => ({
    ...record,
    todayRewardCount: record.todayRewardCount + 1,
  }));

  saveProgress(updated);
  return updated;
}

// ============================================================
// デイリー制限チェック
// ============================================================

/**
 * 本日の再戦報酬が獲得可能かどうか
 */
export function canClaimRepeatReward(
  progress: AdventureProgress,
  npcId: NpcId
): boolean {
  const record = progress.npcRecords[npcId];
  if (!record) return true;

  return record.todayRewardCount < DAILY_BATTLE_LIMIT;
}

/**
 * 本日の残り報酬獲得可能回数
 */
export function getRemainingRewardCount(
  progress: AdventureProgress,
  npcId: NpcId
): number {
  const record = progress.npcRecords[npcId];
  if (!record || (typeof (record as any).dayId === 'number' && (record as any).dayId !== Math.floor((Date.now() + 9 * 3600000) / 86400000))) return DAILY_BATTLE_LIMIT;

  return Math.max(0, DAILY_BATTLE_LIMIT - record.todayRewardCount);
}

// ============================================================
// 統計情報
// ============================================================

/**
 * 総合戦績を取得
 */
export function getTotalRecord(progress: AdventureProgress): {
  battles: number;
  wins: number;
  losses: number;
  winRate: number;
} {
  const { totalBattles, totalWins, totalLosses } = progress;
  const winRate = totalBattles > 0 ? (totalWins / totalBattles) * 100 : 0;

  return {
    battles: totalBattles,
    wins: totalWins,
    losses: totalLosses,
    winRate: Math.round(winRate * 10) / 10,
  };
}

/**
 * NPCとの戦績を取得
 */
export function getNpcStats(
  progress: AdventureProgress,
  npcId: NpcId
): {
  wins: number;
  losses: number;
  winRate: number;
  isCleared: boolean;
} {
  const record = getNpcRecord(progress, npcId);
  const total = record.wins + record.losses;
  const winRate = total > 0 ? (record.wins / total) * 100 : 0;

  return {
    wins: record.wins,
    losses: record.losses,
    winRate: Math.round(winRate * 10) / 10,
    isCleared: isNpcCleared(progress, npcId),
  };
}

/**
 * クリア進捗を取得
 */
export function getClearProgress(progress: AdventureProgress): {
  cleared: number;
  total: number;
  percentage: number;
} {
  const total = 16; // 総NPC数
  const cleared = progress.clearedNpcs.length;
  const percentage = (cleared / total) * 100;

  return {
    cleared,
    total,
    percentage: Math.round(percentage * 10) / 10,
  };
}

// ============================================================
// 全クリア判定
// ============================================================

/**
 * 全NPCをクリアしたかどうか
 */
export function isAllCleared(progress: AdventureProgress): boolean {
  return progress.clearedNpcs.length >= 16;
}

/**
 * 全クリアボーナスを付与済みかどうか
 */
export function hasAllClearBonus(progress: AdventureProgress): boolean {
  return progress.titles.includes('マジックスピリット・マスター');
}
